import React, { useEffect, useRef, useState } from "react";
import { format, parseISO } from "date-fns";

import { makeStyles } from "@material-ui/core/styles";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import Button from "@material-ui/core/Button";
import Typography from "@material-ui/core/Typography";
import CircularProgress from "@material-ui/core/CircularProgress";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import ButtonWithSpinner from "../ButtonWithSpinner";

const useStyles = makeStyles(theme => ({
	content: {
		minHeight: 240,
		backgroundColor: theme.palette.action.hover,
		display: "flex",
		flexDirection: "column",
	},
	row: {
		display: "flex",
		marginBottom: theme.spacing(1),
	},
	rowMine: { justifyContent: "flex-end" },
	bubble: {
		maxWidth: "80%",
		padding: theme.spacing(0.75, 1.25),
		borderRadius: 8,
		backgroundColor: theme.palette.background.paper,
		boxShadow: theme.shadows[1],
		whiteSpace: "pre-wrap",
		wordBreak: "break-word",
	},
	bubbleMine: { backgroundColor: "#dcf8c6", color: "#1b1b1b" },
	who: { fontSize: 11, fontWeight: 600, opacity: 0.7 },
	time: { fontSize: 11, opacity: 0.6, textAlign: "right" },
	note: { textAlign: "center", marginBottom: theme.spacing(1) },
	center: { margin: "auto" },
	deleted: { fontStyle: "italic", opacity: 0.7 },
}));

const MessageBubble = ({ message, classes }) => {
	const mine = message.fromMe;
	const hasMedia = message.mediaType && message.mediaType !== "chat";
	let time = "";
	try {
		time = format(parseISO(message.createdAt), "dd/MM HH:mm");
	} catch (e) {
		time = "";
	}

	return (
		<div className={`${classes.row} ${mine ? classes.rowMine : ""}`}>
			<div className={`${classes.bubble} ${mine ? classes.bubbleMine : ""}`}>
				<div className={classes.who}>
					{mine ? i18n.t("ticketsList.peek.team") : i18n.t("ticketsList.peek.customer")}
				</div>
				{message.isDeleted ? (
					<div className={classes.deleted}>{i18n.t("ticketsList.peek.deleted")}</div>
				) : (
					<>
						{hasMedia && (
							<div>
								{message.mediaUrl ? (
									<a href={message.mediaUrl} target="_blank" rel="noopener noreferrer">
										{i18n.t("ticketsList.peek.media")} {message.mediaType}
									</a>
								) : (
									`${i18n.t("ticketsList.peek.media")} ${message.mediaType}`
								)}
							</div>
						)}
						{message.body && <div>{message.body}</div>}
					</>
				)}
				<div className={classes.time}>{time}</div>
			</div>
		</div>
	);
};

const TicketPeekDialog = ({ ticket, open, onClose, onAccept, accepting }) => {
	const classes = useStyles();
	const [loading, setLoading] = useState(false);
	const [data, setData] = useState(null);
	const endRef = useRef(null);

	useEffect(() => {
		if (!open || !ticket) return undefined;
		let cancelled = false;
		setLoading(true);
		setData(null);
		(async () => {
			try {
				const { data } = await api.get(`/tickets/${ticket.id}/peek`, { params: { limit: 100 } });
				if (!cancelled) setData(data);
			} catch (err) {
				if (!cancelled) {
					toastError(err);
					onClose();
				}
			}
			if (!cancelled) setLoading(false);
		})();
		return () => {
			cancelled = true;
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open, ticket?.id]);

	useEffect(() => {
		if (data && endRef.current) endRef.current.scrollIntoView();
	}, [data]);

	if (!ticket) return null;

	const info = data?.ticket || ticket;
	const queueName = info.queue?.name || i18n.t("ticketsList.peek.noQueue");
	const status = info.botActive ? i18n.t("ticketsList.peek.botActive") : i18n.t("ticketsList.peek.waiting");

	return (
		<Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth scroll="paper">
			<DialogTitle>
				{ticket.contact?.name}
				<Typography variant="body2" color="textSecondary">
					{ticket.contact?.number} · {queueName} · {status}
				</Typography>
				<Typography variant="caption" color="textSecondary">
					{i18n.t("ticketsList.peek.subtitle")}
				</Typography>
			</DialogTitle>
			<DialogContent dividers className={classes.content}>
				{loading && <CircularProgress className={classes.center} />}
				{data && data.messages.length === 0 && (
					<Typography className={classes.center} color="textSecondary">
						{i18n.t("ticketsList.peek.empty")}
					</Typography>
				)}
				{data && data.hasMore && (
					<Typography variant="caption" color="textSecondary" className={classes.note}>
						{i18n.t("ticketsList.peek.latest")}
					</Typography>
				)}
				{data && data.messages.map(m => <MessageBubble key={m.id} message={m} classes={classes} />)}
				<div ref={endRef} />
			</DialogContent>
			<DialogActions>
				<Button onClick={onClose}>{i18n.t("ticketsList.peek.close")}</Button>
				<ButtonWithSpinner color="primary" variant="contained" loading={accepting} onClick={onAccept}>
					{i18n.t("ticketsList.buttons.accept")}
				</ButtonWithSpinner>
			</DialogActions>
		</Dialog>
	);
};

export default TicketPeekDialog;
